import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ITier } from 'features/ShoppingCart/types';
import type { StayQuote } from 'utils/cart/stayQuote';
import { ResourceGroup } from 'types/listings';
import { ResourceParagraph } from 'components/theme/Typography/ResourceTypography';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import { useCurrencyFormatter } from 'hooks/useCurrencyFormatter';
import { currencyFormat, getPercentageAmount } from 'utils';
import { Flex, Sep } from 'components/common/styled/common-styled';
import { TaxSchemaV3 } from '@kouto/types';
import { calculateContractAmounts, ContractTaxLine } from '@kouto/cart-prices';
import z from 'zod';

type TaxesV3 = z.infer<typeof TaxSchemaV3>;
interface Props {
  priceTiers: Array<ITier>;
  /** The server's price for a picked stay; when present its figures stand in for the sums below. */
  stayQuote?: StayQuote | null;
  title: string;
  taxes: ResourceGroup['taxes'] | TaxesV3[];
  coverImageUrl: string;
  applyFlatFeeToEachTier: boolean;
  coverAltText?: string | null;
}

const sumUpTotal = (tiers: ITier[]): number => {
  if (!tiers.length) {
    return 0;
  }
  return tiers.reduce<number>((sum, tier) => {
    const summedAmount = sum + tier.price * tier.selectedNumber;
    return summedAmount;
  }, 0);
};

const PriceTiersSummary = ({
  priceTiers,
  stayQuote = null,
  title,
  taxes,
  coverImageUrl,
  applyFlatFeeToEachTier,
  coverAltText,
}: Props) => {
  const currency = useBrandCurrency();
  const { formattedNumber } = useCurrencyFormatter();
  const { t: translate } = useTranslation();

  const includedTaxes = useMemo(() => {
    return (
      taxes
        ?.filter((tax) => tax.includedInPrice)
        .map((t) => ({
          name: 'name' in t ? t.name : 'Taxes',
          percentage: 'percentage' in t && t.percentage ? t.percentage : 0,
        })) ?? []
    );
  }, [taxes]);

  const nonIncludedTaxes = useMemo(() => {
    return taxes?.filter((tax) => !tax.includedInPrice) ?? [];
  }, [taxes]);

  const mapNonIncludedTaxes = useMemo(() => {
    const total = sumUpTotal(priceTiers);
    const selectedPriceTiersCount = priceTiers.reduce(
      (acc, tier) => acc + tier.selectedNumber,
      0,
    );
    if (selectedPriceTiersCount === 0 || total === 0) {
      return [];
    }
    /* eslint-disable no-nested-ternary */
    return nonIncludedTaxes
      .map((tax) => {
        const taxPercentage =
          'percentage' in tax && tax.percentage ? tax.percentage : 0;
        const taxAmount = 'amount' in tax && tax.amount ? tax.amount : 0;

        return {
          ...tax,
          amountFee: taxPercentage
            ? getPercentageAmount(taxPercentage)(total)
            : applyFlatFeeToEachTier
            ? taxAmount * selectedPriceTiersCount
            : taxAmount ?? 0,
        };
      })
      .filter((t) => !!t.amountFee);
    /* eslint-enable no-nested-ternary */
  }, [priceTiers, nonIncludedTaxes, applyFlatFeeToEachTier]);

  const priceTiersAmount = priceTiers.reduce(
    (acc, tier) => acc + tier.price * tier.selectedNumber,
    0,
  );

  const calculation = calculateContractAmounts(
    priceTiersAmount,
    taxes as ContractTaxLine[],
  );

  // Create a Map of tax id to computed amount for efficient lookup
  const taxAmountsById = new Map(
    calculation.breakdown.map((b) => [b.id, b.computedAmount]),
  );

  // Update mapNonIncludedTaxes with the computed amounts. A quoted stay's taxes are the server's:
  // levied per date and per unit, which the sums above cannot reproduce.
  const quotedTaxById = new Map(
    (stayQuote?.taxes ?? []).map((tax) => [tax.id, tax.amount]),
  );
  const updatedMapNonIncludedTaxes = mapNonIncludedTaxes.map((tax) => ({
    ...tax,
    computedAmount:
      (stayQuote ? quotedTaxById.get(tax.id) : undefined) ??
      taxAmountsById.get(tax.id) ??
      0,
  }));
  const totalAmount = stayQuote ? stayQuote.total : calculation.totalAmount;
  const tierAmount = (tier: ITier): number =>
    stayQuote?.lineSubtotalByTierName[tier.name] ?? tier.price;

  return (
    <SummaryWrapper
      className="summary-wrapper price-tier-summary__summary-wrapper"
      data-testid="price-tier-summary"
    >
      <WrapperDiv>
        <CoverImage
          className="resource-group-summary__resource-image"
          src={coverImageUrl}
          alt={coverAltText || title}
          data-testid="picture-component"
        />
      </WrapperDiv>
      <Sep />
      <WrapperDiv>
        <Flex direction="column" alignItem="flex-start" gap={20}>
          {priceTiers.map((tier) => {
            if (!tier.selectedNumber) {
              return null;
            }
            return (
              <Flex key={tier.name} direction="row" width="100%">
                <ResourceParagraph
                  className="resource-group-summary__price-tier"
                  data-testid={`price-tier-summary-tier-${tier.name}`}
                  color="var(--way-colors-secondaryTextColor)"
                  fontSize="14px"
                  fontWeight="400"
                >
                  {tier.name} (x{tier.selectedNumber})
                </ResourceParagraph>
                {currency && (
                  <ResourceParagraph
                    className="resource-group-summary__tier-amount"
                    data-testid={`price-tier-summary-tier-amount-${tier.name}`}
                    color="var(--way-colors-secondaryTextColor)"
                    fontSize="14px"
                    fontWeight="400"
                  >
                    {currencyFormat(currency)(
                      formattedNumber(tierAmount(tier)),
                    )}
                  </ResourceParagraph>
                )}
              </Flex>
            );
          })}
          {nonIncludedTaxes.length >= 1 &&
            updatedMapNonIncludedTaxes.map((tax) => {
              return (
                <Flex key={tax.id} direction="row" width="100%">
                  <ResourceParagraph
                    className="resource-group-summary__tax"
                    data-testid={`price-tier-summary-tax-${tax.id}`}
                    color="var(--way-colors-secondaryTextColor)"
                    fontSize="14px"
                    fontWeight="400"
                  >
                    {'name' in tax ? tax.name : 'Taxes'}
                  </ResourceParagraph>
                  {currency && (
                    <ResourceParagraph
                      className="resource-group-summary__tax-amount"
                      data-testid={`price-tier-summary-tax-amount-${tax.id}`}
                      color="var(--way-colors-secondaryTextColor)"
                      fontSize="14px"
                      fontWeight="400"
                    >
                      {currencyFormat(currency)(
                        formattedNumber(tax.computedAmount),
                      )}
                    </ResourceParagraph>
                  )}
                </Flex>
              );
            })}

          <Flex direction="row" width="100%">
            <ResourceParagraph
              className="resource-group-summary__total"
              data-testid="price-tier-summary-total"
              color="var(--way-colors-secondaryTextColor)"
              fontSize="14px"
              fontWeight="700"
            >
              {translate('total')}:
            </ResourceParagraph>
            {currency && (
              <ResourceParagraph
                className="resource-group-summary__total-amount"
                data-testid="price-tier-summary-total-amount"
                color="var(--way-colors-secondaryTextColor)"
                fontSize="14px"
                fontWeight="700"
              >
                {totalAmount > 0 && priceTiersAmount > 0
                  ? currencyFormat(currency)(formattedNumber(totalAmount))
                  : '-'}
              </ResourceParagraph>
            )}
          </Flex>
          {totalAmount > 0 && includedTaxes.length > 0 && (
            <Flex direction="row" width="100%">
              <ResourceParagraph
                className="resource-group-summary__included-taxes"
                data-testid="price-tier-summary-included-taxes"
                color="var(--way-colors-secondaryTextColor)"
                fontSize="12px"
                fontWeight="100"
              >
                * {translate('includes')}{' '}
                {includedTaxes
                  .map((t) => `${t.name} (${t.percentage || 0}%)`)
                  .join(` ${translate('and')} `)}
              </ResourceParagraph>
            </Flex>
          )}
        </Flex>
      </WrapperDiv>
    </SummaryWrapper>
  );
};

const WrapperDiv = styled.div`
  padding: 24px;
`;

const SummaryWrapper = styled.div`
  border: 0.5px solid var(--way-colors-borderColor);
  box-shadow: var(--way-design-boxShadow-s);
  max-width: 350px;
  height: auto;
  background: var(--way-palette-white-10);
  align-self: stretch;
`;

const CoverImage = styled.img`
  object-fit: cover;
  user-select: none;
  max-height: 100%;
  object-position: center;
  height: 254px;
  border-radius: var(--way-design-borderRadiusDefault);
  width: 100%;
`;

export default PriceTiersSummary;
